import React, { useEffect, useRef, useState } from "react";
import { CheckCircle2, Download, Lock, PenLine, Type } from "lucide-react";
import { toast } from "sonner";
import api, { API, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Sign({ token }) {
  const [info, setInfo] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [done, setDone] = useState(false);
  const [mode, setMode] = useState("draw"); // draw | type
  const [name, setName] = useState("");
  const [title, setTitle] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [entityType, setEntityType] = useState("");
  const [partState, setPartState] = useState("");
  const [partAddr, setPartAddr] = useState("");
  const [typed, setTyped] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const hasDrawn = useRef(false);

  useEffect(() => {
    api.get(`/sign/${token}`)
      .then(({ data }) => { setInfo(data); setName(data.participant_name || data.investor_name || ""); if (data.signed) setDone(true); })
      .catch(() => setNotFound(true));
  }, [token]);

  const pos = (e) => {
    const c = canvasRef.current; const r = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: (t.clientX - r.left) * (c.width / r.width), y: (t.clientY - r.top) * (c.height / r.height) };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; const ctx = canvasRef.current.getContext("2d"); const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); const ctx = canvasRef.current.getContext("2d"); ctx.lineWidth = 2.5; ctx.lineCap = "round"; ctx.strokeStyle = "#111"; const p = pos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); hasDrawn.current = true; };
  const end = () => { drawing.current = false; };
  const clearCanvas = () => { const c = canvasRef.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); hasDrawn.current = false; };

  const submit = async () => {
    if (!name.trim()) return toast.error("Enter your full legal name");
    let signature_type = mode, signature_data = "";
    if (mode === "draw") {
      if (!hasDrawn.current) return toast.error("Please draw your signature");
      signature_data = canvasRef.current.toDataURL("image/png");
    } else {
      if (!typed.trim()) return toast.error("Type your signature");
      signature_data = typed.trim();
    }
    setSubmitting(true);
    try {
      await api.post(`/sign/${token}`, { participant_name: name.trim(), participant_title: title.trim(), signed_date: date, eff_date: date, entity_type: entityType.trim(), part_state: partState.trim(), part_addr: partAddr.trim(), signature_type, signature_data });
      setDone(true);
      toast.success("Agreement executed");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (notFound) return <Shell><div className="mono text-sm text-muted-foreground">This signing link is invalid or has expired.</div></Shell>;
  if (!info) return <Shell><div className="mono text-xs text-muted-foreground animate-pulse">Loading agreement…</div></Shell>;

  if (done) return (
    <Shell>
      <div data-testid="sign-complete" className="border border-black bg-black text-white p-8 max-w-xl">
        <CheckCircle2 size={32} className="mb-4" />
        <h1 className="text-2xl font-black tracking-tighter">Agreement Executed</h1>
        <p className="mt-3 font-light text-neutral-300">Your Participation Agreement for <strong className="text-white">{info.deal_name}</strong> is fully executed. A copy has been emailed to you and saved to your account.</p>
        <a data-testid="sign-download" href={`${API}/sign/${token}/document.pdf`} className="mt-6 inline-flex items-center gap-2 rounded-none bg-white text-black hover:bg-transparent hover:text-white border border-white transition-colors h-12 px-8 mono text-xs uppercase tracking-[0.2em]">
          <Download size={16} /> Download Executed Copy
        </a>
      </div>
    </Shell>
  );

  return (
    <Shell>
      <div className="max-w-3xl">
        <div className="inline-flex items-center gap-2 border border-black px-3 py-1 mb-6">
          <Lock size={12} /><span className="mono text-[10px] uppercase tracking-[0.3em]">Confidential · For {info.investor_name}</span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-black tracking-tighter">Participation Agreement</h1>
        <p className="mono text-xs text-muted-foreground mt-2">{info.company} · {info.deal_name} · Countersigned by {info.sponsor_name}, {info.sponsor_title}</p>

        <div className="border border-black mt-8">
          <div className="p-3 border-b border-black bg-muted flex items-center justify-between">
            <span className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Agreement Document · Read-only preview</span>
            <a href={`${API}/agreement/blank.pdf`} target="_blank" rel="noreferrer" className="mono text-[10px] uppercase tracking-[0.15em] underline underline-offset-2">Open in new tab</a>
          </div>
          <div data-testid="preview-note" className="px-3 py-2 border-b border-black bg-black text-white mono text-[10px] tracking-[0.1em] leading-relaxed">
            This is a read-only copy for your review — you do <strong>not</strong> need to click or type anything inside the document. Just answer the questions below and we'll fill it in and sign it for you.
          </div>
          <iframe data-testid="agreement-iframe" title="Participation Agreement" src={`${API}/agreement/blank.pdf`} className="w-full h-[460px] bg-white" />
        </div>

        <div className="mt-10 border border-black p-6 space-y-6">
          <h2 className="text-xl font-black tracking-tighter">Sign & Execute</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="mono text-xs uppercase tracking-[0.2em]">Full legal name</Label>
              <Input data-testid="sign-name" value={name} onChange={(e) => setName(e.target.value)} className="rounded-none border-black h-11 mt-2 focus-visible:ring-0 focus-visible:border-black" />
            </div>
            <div>
              <Label className="mono text-xs uppercase tracking-[0.2em]">Title (if signing for an entity)</Label>
              <Input data-testid="sign-title" value={title} onChange={(e) => setTitle(e.target.value)} className="rounded-none border-black h-11 mt-2 focus-visible:ring-0 focus-visible:border-black" placeholder="Optional" />
            </div>
            <div>
              <Label className="mono text-xs uppercase tracking-[0.2em]">Date</Label>
              <Input data-testid="sign-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-none border-black h-11 mt-2 focus-visible:ring-0 focus-visible:border-black" />
            </div>
            <div>
              <Label className="mono text-xs uppercase tracking-[0.2em]">You are (entity type)</Label>
              <Input data-testid="sign-entity" value={entityType} onChange={(e) => setEntityType(e.target.value)} className="rounded-none border-black h-11 mt-2 focus-visible:ring-0 focus-visible:border-black" placeholder="an individual / a limited liability company" />
            </div>
            <div>
              <Label className="mono text-xs uppercase tracking-[0.2em]">State</Label>
              <Input data-testid="sign-state" value={partState} onChange={(e) => setPartState(e.target.value)} className="rounded-none border-black h-11 mt-2 focus-visible:ring-0 focus-visible:border-black" placeholder="e.g. New York" />
            </div>
            <div className="sm:col-span-2">
              <Label className="mono text-xs uppercase tracking-[0.2em]">Address</Label>
              <Input data-testid="sign-address" value={partAddr} onChange={(e) => setPartAddr(e.target.value)} className="rounded-none border-black h-11 mt-2 focus-visible:ring-0 focus-visible:border-black" placeholder="Street, City, State ZIP" />
            </div>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-3">
              <Label className="mono text-xs uppercase tracking-[0.2em]">Signature</Label>
              <div className="flex border border-black ml-2">
                <button type="button" data-testid="sig-mode-draw" onClick={() => setMode("draw")} className={`inline-flex items-center gap-1 mono text-[10px] uppercase tracking-[0.15em] px-3 h-8 ${mode === "draw" ? "bg-black text-white" : "hover:bg-muted"}`}><PenLine size={12} /> Draw</button>
                <button type="button" data-testid="sig-mode-type" onClick={() => setMode("type")} className={`inline-flex items-center gap-1 mono text-[10px] uppercase tracking-[0.15em] px-3 h-8 ${mode === "type" ? "bg-black text-white" : "hover:bg-muted"}`}><Type size={12} /> Type</button>
              </div>
            </div>
            {mode === "draw" ? (
              <div>
                <canvas ref={canvasRef} data-testid="sign-canvas" width={640} height={180}
                  className="w-full border border-black bg-white touch-none cursor-crosshair"
                  onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
                  onTouchStart={start} onTouchMove={move} onTouchEnd={end} />
                <button type="button" data-testid="sign-clear" onClick={clearCanvas} className="mt-2 mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground hover:text-black underline underline-offset-2">Clear</button>
              </div>
            ) : (
              <Input data-testid="sign-typed" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="Type your name as signature" className="rounded-none border-black h-14 text-2xl italic focus-visible:ring-0 focus-visible:border-black" style={{ fontFamily: "Georgia, serif" }} />
            )}
          </div>

          <p className="mono text-[11px] text-muted-foreground">By signing, you agree to the terms of this Participation Agreement with {info.company}.</p>
          <Button data-testid="sign-submit" onClick={submit} disabled={submitting} className="rounded-none bg-black text-white hover:bg-white hover:text-black border border-black transition-colors h-13 px-10 py-4 mono text-xs uppercase tracking-[0.25em]">
            {submitting ? "Executing…" : "Sign & Execute Agreement"}
          </Button>
        </div>
      </div>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-black">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center">
          <span className="mono text-sm font-semibold tracking-[0.18em]">CANIHAVEMONEY&nbsp;LLC</span>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-6 py-12">{children}</main>
    </div>
  );
}
